export const CS2_PROVIDER_SCHEMA = 'csgo2-radar/provider-frame/1';
export const CS2_PROVIDER_TYPE = 'game_data';
export const CS2_PROVIDER_GAME_ID = 'csgo2';
export const CS2_PROVIDER_MAX_BYTES = 1024 * 1024;

export const CS2_PROVIDER_LIMITS = Object.freeze({
  players: 128,
  projectiles: 512,
  droppedWeapons: 1024,
});

const MAP_ID_PATTERN = /^(?:de|cs|ar|dz)_[a-z0-9_]{1,60}$/;
const ENTITY_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,64}$/;
const MODEL_FAMILY_PATTERN = /^[a-z0-9_:-]{1,64}$/;
const POSITION_LIMIT = 10_000_000;

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const finite = (value) => typeof value === 'number' && Number.isFinite(value);
const integer = (value, min, max) => Number.isSafeInteger(value) && value >= min && value <= max;
const text = (value, max) => typeof value === 'string' && value.length <= max;
const entityId = (value) => (
  (Number.isSafeInteger(value) && value >= 0)
  || (typeof value === 'string' && ENTITY_ID_PATTERN.test(value))
);

function push(errors, code, path, message) {
  errors.push({ code, path, message });
}

function exactKeys(value, allowed, path, errors) {
  if (!isRecord(value)) return;
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) push(errors, 'CS2RADAR-PROTOCOL-012', `${path}.${key}`, 'unexpected field');
  }
}

function requireKeys(value, required, path, errors) {
  if (!isRecord(value)) return;
  for (const key of required) {
    if (!(key in value)) push(errors, 'CS2RADAR-PROTOCOL-013', `${path}.${key}`, 'required field missing');
  }
}

function validatePosition(value, path, errors, nullable = false) {
  if (nullable && value === null) return;
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-020', path, 'position object required');
    return;
  }
  exactKeys(value, new Set(['x', 'y', 'z']), path, errors);
  requireKeys(value, ['x', 'y', 'z'], path, errors);
  for (const axis of ['x', 'y', 'z']) {
    if (!finite(value[axis]) || Math.abs(value[axis]) > POSITION_LIMIT) {
      push(errors, 'CS2RADAR-PROTOCOL-021', `${path}.${axis}`, 'finite coordinate outside accepted range');
    }
  }
}

function validateWeapon(value, path, errors) {
  if (value === null) return;
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-030', path, 'weapon object or null required');
    return;
  }
  exactKeys(value, new Set(['name', 'ammo', 'reserve']), path, errors);
  requireKeys(value, ['name'], path, errors);
  if (!text(value.name, 64) || value.name.length === 0) push(errors, 'CS2RADAR-PROTOCOL-031', `${path}.name`, 'weapon name required');
  if ('ammo' in value && !integer(value.ammo, 0, 1000)) push(errors, 'CS2RADAR-PROTOCOL-032', `${path}.ammo`, 'invalid ammo');
  if ('reserve' in value && !integer(value.reserve, 0, 5000)) push(errors, 'CS2RADAR-PROTOCOL-033', `${path}.reserve`, 'invalid reserve ammo');
}

function validatePlayer(value, index, errors, ids) {
  const path = `$.data.players[${index}]`;
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-040', path, 'player object required');
    return;
  }
  exactKeys(value, new Set([
    'id', 'name', 'team', 'health', 'armor', 'dead', 'local', 'angle',
    'helmet', 'defuser', 'money', 'modelName', 'modelFamily', 'stance',
    'movementState', 'snapshotAgeMs', 'dataConfidence', 'position',
    'velocity', 'weapon', 'actionState', 'actionSequence',
  ]), path, errors);
  requireKeys(value, ['id', 'name', 'team', 'health', 'armor', 'dead', 'local', 'angle', 'position', 'weapon'], path, errors);
  if (!entityId(value.id)) push(errors, 'CS2RADAR-PROTOCOL-041', `${path}.id`, 'invalid player id');
  else {
    const key = `${typeof value.id}:${value.id}`;
    if (ids.has(key)) push(errors, 'CS2RADAR-PROTOCOL-042', `${path}.id`, 'duplicate player id');
    ids.add(key);
  }
  if (!text(value.name, 96)) push(errors, 'CS2RADAR-PROTOCOL-043', `${path}.name`, 'invalid player name');
  if (!integer(value.team, 0, 4)) push(errors, 'CS2RADAR-PROTOCOL-044', `${path}.team`, 'invalid team');
  if (!integer(value.health, 0, 500)) push(errors, 'CS2RADAR-PROTOCOL-045', `${path}.health`, 'invalid health');
  if (!integer(value.armor, 0, 500)) push(errors, 'CS2RADAR-PROTOCOL-046', `${path}.armor`, 'invalid armor');
  if (typeof value.dead !== 'boolean') push(errors, 'CS2RADAR-PROTOCOL-047', `${path}.dead`, 'boolean required');
  if (typeof value.local !== 'boolean') push(errors, 'CS2RADAR-PROTOCOL-048', `${path}.local`, 'boolean required');
  if (!finite(value.angle) || Math.abs(value.angle) > 360_000) push(errors, 'CS2RADAR-PROTOCOL-049', `${path}.angle`, 'invalid angle');
  for (const key of ['helmet', 'defuser']) {
    if (key in value && typeof value[key] !== 'boolean') push(errors, 'CS2RADAR-PROTOCOL-050', `${path}.${key}`, 'boolean required');
  }
  if ('money' in value && !integer(value.money, 0, 1_000_000)) push(errors, 'CS2RADAR-PROTOCOL-051', `${path}.money`, 'invalid money');
  if ('modelName' in value && (!text(value.modelName, 192) || value.modelName.length === 0)) {
    push(errors, 'CS2RADAR-PROTOCOL-052', `${path}.modelName`, 'invalid model name');
  }
  if ('modelFamily' in value && (typeof value.modelFamily !== 'string'
    || !MODEL_FAMILY_PATTERN.test(value.modelFamily))) {
    push(errors, 'CS2RADAR-PROTOCOL-053', `${path}.modelFamily`, 'invalid model family');
  }
  if ('movementState' in value
    && !['idle', 'walking', 'running', 'airborne', 'dead'].includes(value.movementState)) {
    push(errors, 'CS2RADAR-PROTOCOL-054', `${path}.movementState`, 'invalid movement state');
  }
  if ('snapshotAgeMs' in value && !integer(value.snapshotAgeMs, 0, 600_000)) {
    push(errors, 'CS2RADAR-PROTOCOL-055', `${path}.snapshotAgeMs`, 'invalid snapshot age');
  }
  if ('dataConfidence' in value && !['fresh', 'stale'].includes(value.dataConfidence)) {
    push(errors, 'CS2RADAR-PROTOCOL-056', `${path}.dataConfidence`, 'invalid data confidence');
  }
  if ('stance' in value && !['standing', 'crouching', 'unknown'].includes(value.stance)) {
    push(errors, 'CS2RADAR-PROTOCOL-057', `${path}.stance`, 'invalid stance');
  }
  if ('actionState' in value
    && !['hold', 'fire', 'reload', 'scoped', 'defuse', 'flashed', 'dead'].includes(value.actionState)) {
    push(errors, 'CS2RADAR-PROTOCOL-058', `${path}.actionState`, 'invalid action state');
  }
  if ('actionSequence' in value
    && !integer(value.actionSequence, 0, Number.MAX_SAFE_INTEGER)) {
    push(errors, 'CS2RADAR-PROTOCOL-059', `${path}.actionSequence`, 'invalid action sequence');
  }
  validatePosition(value.position, `${path}.position`, errors);
  if ('velocity' in value) validatePosition(value.velocity, `${path}.velocity`, errors);
  validateWeapon(value.weapon, `${path}.weapon`, errors);
}

function validateBomb(value, errors) {
  const path = '$.data.bomb';
  if (value === null) return;
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-060', path, 'bomb object or null required');
    return;
  }
  exactKeys(value, new Set(['id', 'state', 'carrierId', 'position', 'blowTime', 'defuseTime']), path, errors);
  requireKeys(value, ['state', 'position'], path, errors);
  if ('id' in value && !entityId(value.id)) push(errors, 'CS2RADAR-PROTOCOL-061', `${path}.id`, 'invalid bomb id');
  if (!['carried', 'dropped', 'planting', 'planted', 'defused', 'exploded', 'unknown'].includes(value.state)) {
    push(errors, 'CS2RADAR-PROTOCOL-062', `${path}.state`, 'invalid bomb state');
  }
  if ('carrierId' in value && value.carrierId !== null && !entityId(value.carrierId)) push(errors, 'CS2RADAR-PROTOCOL-063', `${path}.carrierId`, 'invalid carrier id');
  validatePosition(value.position, `${path}.position`, errors, true);
  for (const key of ['blowTime', 'defuseTime']) {
    if (key in value && value[key] !== null && (!finite(value[key]) || value[key] < 0)) push(errors, 'CS2RADAR-PROTOCOL-064', `${path}.${key}`, 'invalid timer');
  }
}

function validateProjectile(value, index, errors) {
  const path = `$.data.projectiles[${index}]`;
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-070', path, 'projectile object required');
    return;
  }
  exactKeys(value, new Set(['id', 'type', 'team', 'ownerId', 'exploded', 'position']), path, errors);
  requireKeys(value, ['id', 'type', 'team', 'exploded', 'position'], path, errors);
  if (!entityId(value.id)) push(errors, 'CS2RADAR-PROTOCOL-071', `${path}.id`, 'invalid projectile id');
  if (!text(value.type, 64) || value.type.length === 0) push(errors, 'CS2RADAR-PROTOCOL-072', `${path}.type`, 'projectile type required');
  if (!integer(value.team, 0, 4)) push(errors, 'CS2RADAR-PROTOCOL-073', `${path}.team`, 'invalid team');
  if ('ownerId' in value && value.ownerId !== null && !entityId(value.ownerId)) push(errors, 'CS2RADAR-PROTOCOL-074', `${path}.ownerId`, 'invalid owner id');
  if (typeof value.exploded !== 'boolean') push(errors, 'CS2RADAR-PROTOCOL-075', `${path}.exploded`, 'boolean required');
  validatePosition(value.position, `${path}.position`, errors);
}

function validateDroppedWeapon(value, index, errors) {
  const path = `$.data.droppedWeapons[${index}]`;
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-080', path, 'dropped weapon object required');
    return;
  }
  exactKeys(value, new Set(['id', 'name', 'ownerId', 'ammo', 'position']), path, errors);
  requireKeys(value, ['id', 'name', 'position'], path, errors);
  if (!entityId(value.id)) push(errors, 'CS2RADAR-PROTOCOL-081', `${path}.id`, 'invalid weapon id');
  if (!text(value.name, 64) || value.name.length === 0) push(errors, 'CS2RADAR-PROTOCOL-082', `${path}.name`, 'weapon name required');
  if ('ownerId' in value && value.ownerId !== null && !entityId(value.ownerId)) push(errors, 'CS2RADAR-PROTOCOL-083', `${path}.ownerId`, 'invalid owner id');
  if ('ammo' in value && !integer(value.ammo, 0, 1000)) push(errors, 'CS2RADAR-PROTOCOL-084', `${path}.ammo`, 'invalid ammo');
  validatePosition(value.position, `${path}.position`, errors);
}

function validateCapabilities(value, errors) {
  const path = '$.capabilities';
  if (!isRecord(value)) {
    push(errors, 'CS2RADAR-PROTOCOL-130', path, 'capabilities object required');
    return;
  }
  exactKeys(value, new Set(['resourceBundleVersions', 'views', 'mapBuildId']), path, errors);
  if ('resourceBundleVersions' in value) {
    if (!Array.isArray(value.resourceBundleVersions)
      || value.resourceBundleVersions.length > 8
      || value.resourceBundleVersions.some((version) => !integer(version, 1, 32))) {
      push(errors, 'CS2RADAR-PROTOCOL-131', `${path}.resourceBundleVersions`, 'invalid resource bundle versions');
    }
  }
  if ('views' in value) {
    if (!Array.isArray(value.views) || value.views.length > 2
      || value.views.some((view) => !['2d', '3d'].includes(view))) {
      push(errors, 'CS2RADAR-PROTOCOL-132', `${path}.views`, 'invalid radar views');
    }
  }
  if ('mapBuildId' in value && !text(value.mapBuildId, 64)) {
    push(errors, 'CS2RADAR-PROTOCOL-133', `${path}.mapBuildId`, 'invalid map build id');
  }
}

export function validateCs2ProviderFrame(frame, options = {}) {
  const errors = [];
  const byteLength = options.byteLength ?? null;
  if (byteLength !== null && (!Number.isSafeInteger(byteLength) || byteLength < 0 || byteLength > CS2_PROVIDER_MAX_BYTES)) {
    push(errors, 'CS2RADAR-PROTOCOL-002', '$', 'payload exceeds 1 MiB');
  }
  if (!isRecord(frame)) {
    push(errors, 'CS2RADAR-PROTOCOL-001', '$', 'JSON object required');
    return { ok: false, errors };
  }
  exactKeys(frame, new Set(['type', 'schemaVersion', 'gameId', 'sequence', 'timestamp', 'capabilities', 'data']), '$', errors);
  requireKeys(frame, ['type', 'schemaVersion', 'gameId', 'sequence', 'timestamp', 'data'], '$', errors);
  if (frame.type !== CS2_PROVIDER_TYPE) push(errors, 'CS2RADAR-PROTOCOL-003', '$.type', `expected ${CS2_PROVIDER_TYPE}`);
  if (frame.schemaVersion !== CS2_PROVIDER_SCHEMA) push(errors, 'CS2RADAR-PROTOCOL-004', '$.schemaVersion', `expected ${CS2_PROVIDER_SCHEMA}`);
  if (frame.gameId !== CS2_PROVIDER_GAME_ID) push(errors, 'CS2RADAR-PROTOCOL-005', '$.gameId', `expected ${CS2_PROVIDER_GAME_ID}`);
  if (!integer(frame.sequence, 0, Number.MAX_SAFE_INTEGER)) push(errors, 'CS2RADAR-PROTOCOL-006', '$.sequence', 'non-negative safe integer required');
  if (!integer(frame.timestamp, 1, Number.MAX_SAFE_INTEGER)) push(errors, 'CS2RADAR-PROTOCOL-007', '$.timestamp', 'positive epoch milliseconds required');
  if ('capabilities' in frame) validateCapabilities(frame.capabilities, errors);

  if (!isRecord(frame.data)) {
    push(errors, 'CS2RADAR-PROTOCOL-010', '$.data', 'data object required');
    return { ok: errors.length === 0, errors };
  }
  exactKeys(frame.data, new Set(['map', 'local', 'round', 'players', 'bomb', 'projectiles', 'droppedWeapons']), '$.data', errors);
  requireKeys(frame.data, ['map', 'local', 'round', 'players', 'bomb', 'projectiles', 'droppedWeapons'], '$.data', errors);

  const map = frame.data.map;
  if (!isRecord(map)) push(errors, 'CS2RADAR-PROTOCOL-090', '$.data.map', 'map object required');
  else {
    exactKeys(map, new Set(['id', 'buildId']), '$.data.map', errors);
    requireKeys(map, ['id'], '$.data.map', errors);
    if (typeof map.id !== 'string' || !MAP_ID_PATTERN.test(map.id)) push(errors, 'CS2RADAR-PROTOCOL-091', '$.data.map.id', 'invalid CS2 map id');
    if ('buildId' in map && !text(map.buildId, 64)) push(errors, 'CS2RADAR-PROTOCOL-092', '$.data.map.buildId', 'invalid build id');
  }

  const local = frame.data.local;
  if (local !== null && !isRecord(local)) push(errors, 'CS2RADAR-PROTOCOL-100', '$.data.local', 'local object or null required');
  else if (isRecord(local)) {
    exactKeys(local, new Set(['playerId', 'team']), '$.data.local', errors);
    requireKeys(local, ['playerId', 'team'], '$.data.local', errors);
    if (!entityId(local.playerId)) push(errors, 'CS2RADAR-PROTOCOL-101', '$.data.local.playerId', 'invalid local player id');
    if (!integer(local.team, 0, 4)) push(errors, 'CS2RADAR-PROTOCOL-102', '$.data.local.team', 'invalid local team');
  }

  const round = frame.data.round;
  if (!isRecord(round)) push(errors, 'CS2RADAR-PROTOCOL-110', '$.data.round', 'round object required');
  else {
    exactKeys(round, new Set(['phase', 'number']), '$.data.round', errors);
    requireKeys(round, ['phase'], '$.data.round', errors);
    if (!text(round.phase, 48)) push(errors, 'CS2RADAR-PROTOCOL-111', '$.data.round.phase', 'invalid round phase');
    if ('number' in round && !integer(round.number, 0, 1000)) push(errors, 'CS2RADAR-PROTOCOL-112', '$.data.round.number', 'invalid round number');
  }

  for (const [name, limit] of Object.entries(CS2_PROVIDER_LIMITS)) {
    const collection = frame.data[name];
    if (!Array.isArray(collection)) push(errors, 'CS2RADAR-PROTOCOL-120', `$.data.${name}`, 'array required');
    else if (collection.length > limit) push(errors, 'CS2RADAR-PROTOCOL-121', `$.data.${name}`, `collection limit ${limit} exceeded`);
  }
  const ids = new Set();
  if (Array.isArray(frame.data.players)) frame.data.players.forEach((value, index) => validatePlayer(value, index, errors, ids));
  validateBomb(frame.data.bomb, errors);
  if (Array.isArray(frame.data.projectiles)) frame.data.projectiles.forEach((value, index) => validateProjectile(value, index, errors));
  if (Array.isArray(frame.data.droppedWeapons)) frame.data.droppedWeapons.forEach((value, index) => validateDroppedWeapon(value, index, errors));

  return { ok: errors.length === 0, errors };
}

export function validateActionSequenceProgression(previousFrame, currentFrame) {
  const errors = [];
  const previousPlayers = previousFrame?.data?.players;
  const currentPlayers = currentFrame?.data?.players;
  if (!Array.isArray(previousPlayers) || !Array.isArray(currentPlayers)) {
    return { ok: true, errors };
  }
  const previousById = new Map(previousPlayers
    .filter((player) => entityId(player?.id) && Number.isSafeInteger(player?.actionSequence))
    .map((player) => [`${typeof player.id}:${player.id}`, player.actionSequence]));
  currentPlayers.forEach((player, index) => {
    if (!entityId(player?.id) || !Number.isSafeInteger(player?.actionSequence)) return;
    const previous = previousById.get(`${typeof player.id}:${player.id}`);
    if (Number.isSafeInteger(previous) && player.actionSequence < previous) {
      push(errors, 'CS2RADAR-PROTOCOL-060A', `$.data.players[${index}].actionSequence`,
        `action sequence must be at least ${previous}`);
    }
  });
  return { ok: errors.length === 0, errors };
}

export function protocolErrorPayload(result, fallbackCode = 'CS2RADAR-PROTOCOL-400') {
  return {
    type: 'protocol_error',
    schemaVersion: CS2_PROVIDER_SCHEMA,
    code: result?.errors?.[0]?.code || fallbackCode,
    errors: result?.errors || [],
  };
}
